import Modal from '@/components/Modal';
import { Flex, Heading, Input, Paragraph, Text } from 'theme-ui';
import { Button } from '@odekoteam/doppio';
import {
  ModalHeaderStyle,
  ModalFooterActions,
} from '@/components/Brand/Styles';
import ModalErrorBanner from '@/components/Brand/ModalErrorBanner';
import { useState } from 'react';
import { isEqualCaseSensitive } from '@/utils/str';
import { useDeleteCollectionPromoSetupMutation } from '@/graphql/types/generated';

export type CollectionInfo = {
  id: string;
  name: string;
};

const DeleteCollectionModal = ({
  visible,
  collection,
  close,
  submit,
}: {
  visible: boolean;
  collection?: CollectionInfo;
  close: () => void;
  submit: () => void;
}) => {
  const [collectionName, setCollectionName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const [, deleteCollectionPromoSetup] =
    useDeleteCollectionPromoSetupMutation();

  const isDisabled =
    isSubmitting ||
    !collection?.name ||
    !isEqualCaseSensitive(collectionName, collection.name);

  const resetState = () => {
    setCollectionName('');
    setErrors([]);
    setIsSubmitting(false);
  };

  const handleCancel = () => {
    resetState();
    close();
  };

  const handleSubmit = async () => {
    const submitErrors: string[] = [];

    try {
      setIsSubmitting(true);
      const { data, error } = await deleteCollectionPromoSetup({
        input: {
          collectionId: collection!.id,
        },
      });
      if (error) {
        submitErrors.push(error.message);
      }

      data?.deleteCollectionPromoSetup?.errors.forEach((e) => {
        submitErrors.push(e.message);
      });
    } catch (e: any) {
      submitErrors.push(e.message);
    } finally {
      setIsSubmitting(false);
    }

    setErrors(submitErrors);
    if (submitErrors.length === 0) {
      submit();
      resetState();
    }
  };

  return (
    <Modal visible={visible}>
      <Heading as="h3" sx={ModalHeaderStyle}>
        Delete Collection
      </Heading>

      <ModalErrorBanner errors={errors} />

      <Flex sx={{ flexDirection: 'column', gap: 3, marginBottom: 6 }}>
        <Paragraph variant="paragraphMd">
          Are you sure you want to delete{' '}
          <Text variant="paragraphExtraBoldMd">{collection?.name}</Text>{' '}
          collection? This action cannot be undone.
        </Paragraph>

        <Paragraph variant="paragraphExtraBoldMd">
          Please write the collection name you are going to delete.
        </Paragraph>
      </Flex>

      <Flex sx={{ flexDirection: 'column', gap: 2 }}>
        <Input
          type="text"
          placeholder="Collection Name"
          value={collectionName}
          onChange={(e) => setCollectionName(e.target.value)}
        />
      </Flex>

      <Flex sx={ModalFooterActions}>
        <Button variant="ghost" onPress={handleCancel}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onPress={handleSubmit}
          isDisabled={isDisabled}
        >
          Delete
        </Button>
      </Flex>
    </Modal>
  );
};

export default DeleteCollectionModal;
